/**
 * Products Cart Selectors
 * Optimized selectors for working with Immutable.js state
 * Minimizes .toJS() conversions to improve performance
 */

/**
 * Select catalog from products cart state
 * Returns Immutable Map structure for efficient access
 *
 * @param {Object} state - Redux state
 * @returns {Map} Catalog as Immutable Map with outgoing/incoming keys
 */
export const selectCatalog = (state) => {
  return state.productsCart?.get('catalog');
};

/**
 * Select catalog by segment from products cart state
 * Only converts to JS for the specific segment needed
 *
 * @param {string} segment - Segment of the journey: 'outgoing' or 'incoming'
 * @returns {Function} Selector function
 */
export const selectCatalogBySegment = (segment = 'outgoing') => {
  return (state) => {
    const catalog = selectCatalog(state);
    return catalog?.get(segment)?.toJS() || [];
  };
};

/**
 * Select selected products from products cart state
 * Returns raw Immutable Map for most efficient access
 *
 * @param {Object} state - Redux state
 * @returns {Map} Selected products as Immutable Map
 */
export const selectSelectedProductsImmutable = (state) => {
  return state.productsCart?.get('selectedProducts');
};

/**
 * Select selected products as plain JavaScript object
 * Use when you need to iterate or process the entire structure
 *
 * @param {Object} state - Redux state
 * @returns {Object} Selected products as plain object
 */
export const selectSelectedProducts = (state) => {
  return selectSelectedProductsImmutable(state)?.toJS() || {};
};

/**
 * Select products for a specific passenger and segment
 * Efficient single conversion for the specific data needed
 *
 * @param {number} passengerIndex - Index of the passenger
 * @param {string} segment - Segment: 'outgoing' or 'incoming'
 * @returns {Function} Selector function
 */
export const selectSelectedProductsByPassengerIndex = (passengerIndex, segment) => {
  return (state) => {
    const selectedProductsMap = selectSelectedProductsImmutable(state);
    return selectedProductsMap?.getIn([String(passengerIndex), segment])?.toJS() || [];
  };
};

/**
 * Check if catalog has products for a specific segment
 * Efficient boolean check without full conversion
 *
 * @param {string} segment - Segment: 'outgoing' or 'incoming'
 * @returns {Function} Selector function
 */
export const selectHasCatalogProducts = (segment = 'outgoing') => {
  return (state) => {
    const catalog = selectCatalog(state);
    const segmentCatalog = catalog?.get(segment);
    return segmentCatalog?.size > 0;
  };
};
